export interface BudgetForecastLine {
  name: string;
  type: 'Recette' | 'Dépense';
  year1: number;
  year2: number;
  year3: number;
  year4: number;
  year5: number;
}

export interface ParseResult {
  success: boolean;
  data?: BudgetForecastLine[];
  errors?: string[];
}

function cleanAmount(value: string): number {
  if (!value || value.trim() === '' || value === '-') {
    return 0;
  }

  const cleaned = value
    .replace(/\s/g, '')
    .replace(/€/g, '')
    .replace(/\u00A0/g, '')
    .replace(/,/g, '.')
    .replace(/[^\d.-]/g, '');

  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

function normalizeType(type: string): 'Recette' | 'Dépense' | null {
  const cleaned = type.trim().toLowerCase();

  if (cleaned.includes('recette') || cleaned === 'revenue' || cleaned === 'income') {
    return 'Recette';
  }

  if (cleaned.includes('pense') || cleaned.includes('depense') ||
      cleaned === 'expense' || cleaned === 'd�pense' ||
      cleaned.startsWith('d') && cleaned.includes('pense')) {
    return 'Dépense';
  }

  return null;
}

function detectDelimiter(firstLine: string): string {
  const semicolonCount = (firstLine.match(/;/g) || []).length;
  const commaCount = (firstLine.match(/,/g) || []).length;

  return semicolonCount > commaCount ? ';' : ',';
}

export function parseBudgetForecastCSV(csvText: string): ParseResult {
  const errors: string[] = [];
  const lines = csvText.split('\n').map(line => line.trim()).filter(line => line);

  if (lines.length < 2) {
    return {
      success: false,
      errors: ['Le fichier CSV est vide ou ne contient pas assez de lignes']
    };
  }

  const delimiter = detectDelimiter(lines[0]);
  const header = lines[0].split(delimiter).map(h => h.trim());

  const expectedHeaders = ['Ligne budgétaire', 'Type', 'Année 1', 'Année 2', 'Année 3', 'Année 4', 'Année 5'];
  const headerValid = header.length >= 7 && (
    header[0].toLowerCase().includes('ligne') ||
    header[0].toLowerCase().includes('budg')
  );

  if (!headerValid) {
    return {
      success: false,
      errors: [`En-têtes du CSV invalides. Format attendu: Ligne budgétaire${delimiter}Type${delimiter}Année 1${delimiter}Année 2${delimiter}Année 3${delimiter}Année 4${delimiter}Année 5`]
    };
  }

  const data: BudgetForecastLine[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const columns = line.split(delimiter).map(col => col.trim());

    if (columns.length < 7) {
      errors.push(`Ligne ${i + 1}: nombre de colonnes insuffisant (${columns.length}/7)`);
      continue;
    }

    const [name, type, year1Str, year2Str, year3Str, year4Str, year5Str] = columns;

    if (!name || name === '') {
      errors.push(`Ligne ${i + 1}: nom de la ligne budgétaire manquant`);
      continue;
    }

    const normalizedType = normalizeType(type);
    if (!normalizedType) {
      errors.push(`Ligne ${i + 1}: type invalide "${type}" (attendu: "Recette" ou "Dépense")`);
      continue;
    }

    const year1 = cleanAmount(year1Str);
    const year2 = cleanAmount(year2Str);
    const year3 = cleanAmount(year3Str);
    const year4 = cleanAmount(year4Str);
    const year5 = cleanAmount(year5Str);

    data.push({
      name,
      type: normalizedType,
      year1,
      year2,
      year3,
      year4,
      year5
    });
  }

  if (data.length === 0) {
    return {
      success: false,
      errors: errors.length > 0 ? errors : ['Aucune donnée valide trouvée dans le fichier']
    };
  }

  if (errors.length > 0 && errors.length === lines.length - 1) {
    return {
      success: false,
      errors
    };
  }

  return {
    success: true,
    data
  };
}

export function downloadTemplate(french: boolean = false) {
  const link = document.createElement('a');
  if (french) {
    link.href = '/template-budget-previsionnel-5ans-fr.csv';
    link.download = 'template-budget-previsionnel-5ans-fr.csv';
  } else {
    link.href = '/template-budget-previsionnel-5ans.csv';
    link.download = 'template-budget-previsionnel-5ans.csv';
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
